"use client";

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ontologyCreateDatabaseConnection,
  ontologyCreateMongodbDatasource,
  ontologyCreateMysqlDatasource,
  ontologyCreatePostgresqlDatasource,
  ontologyCreateSqlserverDatasource,
  ontologyDeleteDataSource,
  ontologyDiscoverDatabaseTables,
  ontologyGetDatasources,
  ontologyListForeignConnections,
  ontologyTestConnection,
  ontologyTestDatabaseConnection,
} from "../sdk";
import { GC_TIME, LOW_CHURN_STALE_TIME } from "../query/config";
import { queryKeys } from "../query/keys";

// Shape we probe on errors thrown by the SDK / fetch layer. The SDK
// narrows to `unknown` so we centralize the cast here instead of
// sprinkling `(err as any)` across catch blocks.
interface HttpLikeError {
  status?: number;
  body?: unknown;
  response?: { data?: unknown };
}

function asHttpLikeError(err: unknown): HttpLikeError {
  return err && typeof err === "object" ? (err as HttpLikeError) : {};
}

export interface DatabaseConnection {
  id: string;
  name: string;
  database_type:
    | "postgresql"
    | "clickhouse"
    | "mysql"
    | "mongodb"
    | "sqlserver"
    | "bigquery"
    | "netsuite";
  host: string;
  port: number;
  database: string;
  username: string;
  status: "connected" | "disconnected" | "error";
  last_tested?: string;
  created_at: string;
  updated_at: string;
}

export interface DatabaseCredentials {
  host?: string;
  port?: number;
  database?: string;
  username?: string;
  password: string;
  ssl_mode?: string;
  connection_params?: Record<string, any>;
}

export interface CreateConnectionRequest {
  name: string;
  database_type:
    | "postgresql"
    | "clickhouse"
    | "mysql"
    | "mongodb"
    | "sqlserver"
    | "bigquery"
    | "netsuite";
  credentials: DatabaseCredentials;
}

export interface DiscoveredSchema {
  schema_name: string;
  table_count: number;
  tables: Array<{
    table_name: string;
    row_count?: number;
    size_bytes?: number;
  }>;
}

export interface DiscoverSchemasResult {
  schemas: DiscoveredSchema[];
  database_info: {
    database_type?: string;
    total_tables?: number;
    discovered_at?: string;
  };
}

export interface TestConnectionResult {
  status?: "ok" | "error" | string;
  message?: string;
}

export interface TestConnectionRequest {
  database_type:
    | "postgresql"
    | "clickhouse"
    | "mysql"
    | "mongodb"
    | "sqlserver"
    | "bigquery"
    | "netsuite";
  credentials: DatabaseCredentials;
}

interface UseDatabaseConnectionsResult {
  connections: DatabaseConnection[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseDatabaseConnectionMutationsResult {
  testConnection: (
    request: TestConnectionRequest,
  ) => Promise<TestConnectionResult>;
  createConnection: (
    request: CreateConnectionRequest,
  ) => Promise<DatabaseConnection>;
  testExistingConnection: (
    connectionId: string,
  ) => Promise<TestConnectionResult>;
  deleteConnection: (connectionId: string) => Promise<void>;
  discoverSchemas: (
    request: TestConnectionRequest,
  ) => Promise<DiscoverSchemasResult>;
}

async function fetchDatabaseConnections(
  orgId: string,
): Promise<DatabaseConnection[]> {
  const [datasourcesRes, foreignConnsRes] = await Promise.all([
    ontologyGetDatasources({ query: { org_id: orgId } }),
    ontologyListForeignConnections({
      query: { org_id: orgId, federation_enabled_only: false },
    }).catch(() => ({ data: { connections: [] } })),
  ]);

  const datasources = datasourcesRes.data || [];
  if (!Array.isArray(datasources)) return [];

  const federatedDatasourceIds = new Set(
    (foreignConnsRes.data?.connections ?? [])
      .filter((c) => c.federation_enabled)
      .map((c) => c.datasource_id),
  );

  return datasources.map((datasource: any) => {
    const isBigQuery = datasource.type === "bigquery";
    const isNetSuite = datasource.type === "netsuite";
    const accountId = datasource.config?.account_id || "";

    let host = datasource.config?.host || "";
    let port = datasource.config?.port || 5432;
    let database = datasource.config?.database || "";
    let username = datasource.config?.username || "";

    if (isBigQuery) {
      host = "bigquery.googleapis.com";
      port = 443;
      database = datasource.config?.project_id || "";
      username = "";
    } else if (isNetSuite) {
      host = datasource.config?.host || `${accountId}.connect.api.netsuite.com`;
      port = datasource.config?.port || 1708;
      database = `Account ${accountId}`;
      username = datasource.config?.username || "";
    }

    return {
      id: datasource.id,
      name: datasource.name,
      database_type: datasource.type,
      host,
      port,
      database,
      username,
      status: (datasource.has_lakehouse_migration ||
      federatedDatasourceIds.has(datasource.id)
        ? "connected"
        : "disconnected") as DatabaseConnection["status"],
      last_tested: datasource.edited_at,
      created_at: datasource.created_at,
      updated_at: datasource.edited_at || datasource.created_at,
      credentials: datasource.config,
    };
  });
}

/**
 * Read hook for the org's database connections. Cached via react-query
 * so revisits to /sources don't refetch and flicker.
 */
export function useDatabaseConnections(
  orgId?: string,
): UseDatabaseConnectionsResult {
  const query = useQuery({
    // Distinct sub-key from `datasources.list` (which OrgProvider populates
    // with raw DataSourceModel[]). Sharing that key caused tiles to render
    // gray clouds on first navigation because the cached shape lacked
    // `database_type`.
    queryKey: queryKeys.datasources.connections({ orgId: orgId ?? "" }),
    queryFn: () => fetchDatabaseConnections(orgId!),
    enabled: !!orgId,
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
  });

  const refetch = useCallback(async () => {
    await query.refetch();
  }, [query]);

  return {
    connections: query.data ?? [],
    loading: query.isPending && !!orgId,
    error: query.error instanceof Error ? query.error.message : null,
    refetch,
  };
}

/**
 * Mutation bundle for database connections. Each mutation invalidates
 * the list query on success so the next render shows fresh data.
 */
export function useDatabaseConnectionMutations(
  orgId?: string,
): UseDatabaseConnectionMutationsResult {
  const queryClient = useQueryClient();

  const invalidateList = useCallback(() => {
    // Use the prefix so both the raw `datasources.list` cache (read by
    // OrgProvider / legacy datasets pages) and the transformed
    // `datasources.connections` cache (read here on Sources) refresh
    // after a create / delete.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.datasources.all,
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.foreignConnections.lists(),
    });
  }, [queryClient]);

  const testConnection = useCallback(async (request: TestConnectionRequest) => {
    const response = await ontologyTestDatabaseConnection({ body: request });
    return (response.data ?? {}) as TestConnectionResult;
  }, []);

  const createConnection = useCallback(
    async (request: CreateConnectionRequest) => {
      try {
        if (!orgId) {
          throw new Error("Organization ID is required to create a connection");
        }

        // Transform request to datasource format and use appropriate endpoint.
        // Do not include created_at/edited_at — backend sets them. id is
        // optional and backend-generated.
        const datasourceRequest = {
          name: request.name,
          type: request.database_type,
          config: request.credentials,
          org_id: orgId,
          description: `${request.database_type.charAt(0).toUpperCase() + request.database_type.slice(1)} datasource created via integration hub`,
        };

        let response;

        switch (request.database_type) {
          case "postgresql": {
            const r = await ontologyCreatePostgresqlDatasource({
              body: datasourceRequest,
            });
            response = r.data;
            break;
          }
          case "mysql": {
            const r = await ontologyCreateMysqlDatasource({
              body: datasourceRequest,
            });
            response = r.data;
            break;
          }
          case "mongodb": {
            const r = await ontologyCreateMongodbDatasource({
              body: datasourceRequest,
            });
            response = r.data;
            break;
          }
          case "clickhouse": {
            // ClickHouse uses the database-integration endpoint which handles
            // datasource creation + foreign connection + schema sync. Querying
            // runs through the lakehouse via read_parquet scan federation.
            const r = await ontologyCreateDatabaseConnection({
              query: { org_id: orgId },
              body: {
                name: request.name,
                database_type: "clickhouse",
                credentials: request.credentials,
              },
            });
            response = r.data;
            break;
          }
          case "sqlserver": {
            const r = await ontologyCreateSqlserverDatasource({
              body: datasourceRequest,
            });
            response = r.data;
            break;
          }
          case "bigquery": {
            // BigQuery uses the database-integration endpoint which handles
            // datasource creation + foreign connection + schema sync.
            const r = await ontologyCreateDatabaseConnection({
              query: { org_id: orgId },
              body: {
                name: request.name,
                database_type: "bigquery",
                credentials: request.credentials,
              },
            });
            response = r.data;
            break;
          }
          case "netsuite": {
            // NetSuite uses the database-integration endpoint which handles
            // datasource creation + foreign connection + schema sync (ETL-only).
            const r = await ontologyCreateDatabaseConnection({
              query: { org_id: orgId },
              body: {
                name: request.name,
                database_type: "netsuite",
                credentials: request.credentials,
              },
            });
            response = r.data;
            break;
          }
          default:
            throw new Error(
              `Unsupported database type: ${request.database_type}`,
            );
        }

        invalidateList();
        return response as DatabaseConnection;
      } catch (err) {
        const httpErr = asHttpLikeError(err);
        console.error("🔴 Error creating connection:", {
          error: err,
          errorType: typeof err,
          message: err instanceof Error ? err.message : "Unknown error",
          status: httpErr.status,
          body: httpErr.body,
          response: httpErr.response,
          request,
          orgId,
        });
        if (httpErr.response?.data) {
          console.error("🔴 Detailed error response:", httpErr.response.data);
        }
        if (httpErr.body) {
          console.error("🔴 Error body:", httpErr.body);
        }
        throw err;
      }
    },
    [invalidateList, orgId],
  );

  const testExistingConnection = useCallback(
    async (connectionId: string) => {
      const apiResponse = await ontologyTestConnection({
        path: { datasource_id: connectionId },
      });
      // Status may have changed — refresh the list so the row reflects it.
      invalidateList();
      return (apiResponse.data ?? {}) as TestConnectionResult;
    },
    [invalidateList],
  );

  const deleteConnection = useCallback(
    async (connectionId: string) => {
      if (!orgId) {
        throw new Error("Organization ID is required to delete a connection");
      }
      try {
        await ontologyDeleteDataSource({
          path: { data_source_id: connectionId },
          query: { org_id: orgId },
        });
      } catch (err) {
        // Treat 404 as success — the record is already gone.
        if (asHttpLikeError(err).status !== 404) {
          throw err;
        }
      }
      invalidateList();
    },
    [invalidateList, orgId],
  );

  const discoverSchemas = useCallback(
    async (request: TestConnectionRequest) => {
      const apiResponse = await ontologyDiscoverDatabaseTables({
        body: {
          database_type: request.database_type,
          credentials: request.credentials,
          schema_name: undefined,
        },
      });

      const response = apiResponse.data;
      if (!response) {
        throw new Error("No data in response");
      }

      const schemaMap = new Map<string, DiscoveredSchema>();

      response.tables?.forEach((table: any) => {
        const schemaName = table.schema_name || "default";
        if (!schemaMap.has(schemaName)) {
          schemaMap.set(schemaName, {
            schema_name: schemaName,
            table_count: 0,
            tables: [],
          });
        }

        const schema = schemaMap.get(schemaName)!;
        schema.table_count += 1;
        schema.tables.push({
          table_name: table.table_name,
          row_count:
            typeof table.row_count === "number" ? table.row_count : undefined,
          size_bytes:
            typeof table.size_bytes === "number" ? table.size_bytes : undefined,
        });
      });

      return {
        schemas: Array.from(schemaMap.values()),
        database_info: {
          database_type: response.database_type,
          total_tables: response.total_tables,
          discovered_at: response.discovered_at,
        },
      };
    },
    [],
  );

  return {
    testConnection,
    createConnection,
    testExistingConnection,
    deleteConnection,
    discoverSchemas,
  };
}
